import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { SiteHeader } from "@/components/site/SiteHeader";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LoginRequiredDialog from "@/components/LoginRequiredDialog";
import { useLoginGuard } from "@/hooks/useLoginGuard";
import {
  ArrowLeft,
  Search,
  ExternalLink,
  Percent,
  Gift,
  ChevronRight,
  ShoppingBag,
  Menu,
  X,
  User,
  LogOut,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const BrandsPage = () => {
  const navigate = useNavigate();
  const { user, logout, token, getAuthHeaders } = useAuth();
  const { brandId } = useParams();
  const { requireLogin, dialogOpen, setDialogOpen, dialogMessage } = useLoginGuard();
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pFilters, setPFilters] = useState({
    search: "",
    category: "",
    minPrice: "",
    maxPrice: "",
    sort: "newest",
    page: 1,
  });
  const PRODUCTS_PER_PAGE = 24;
  const [productsTotal, setProductsTotal] = useState(0);
  const [productCategories, setProductCategories] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (brandId) {
      fetchBrandProducts(brandId);
    } else {
      fetchBrands();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, pFilters.page, pFilters.sort, pFilters.category, pFilters.minPrice, pFilters.maxPrice]);

  // Debounced search
  useEffect(() => {
    if (!brandId) return;
    const t = setTimeout(() => fetchBrandProducts(brandId), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pFilters.search]);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/brands/all`);
      setBrands(res.data.brands || []);
    } catch (error) {
      console.error("Failed to fetch brands:", error);
    }
    setLoading(false);
  };

  const fetchBrandProducts = async (id) => {
    setProductsLoading(true);
    try {
      // Fetch brand details in parallel
      const brandRes = await axios.get(`${API}/brands/${id}`);
      setSelectedBrand(brandRes.data.brand || brandRes.data);
    } catch (error) {
      console.error("Failed to fetch brand:", error);
    }
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PRODUCTS_PER_PAGE));
      params.set("skip", String((pFilters.page - 1) * PRODUCTS_PER_PAGE));
      if (pFilters.search) params.set("search", pFilters.search);
      if (pFilters.category) params.set("category", pFilters.category);
      if (pFilters.minPrice) params.set("min_price", pFilters.minPrice);
      if (pFilters.maxPrice) params.set("max_price", pFilters.maxPrice);
      if (pFilters.sort) params.set("sort", pFilters.sort);
      const res = await axios.get(`${API}/brands/${id}/products?${params.toString()}`);
      setProducts(res.data.products || []);
      setProductsTotal(res.data.total || 0);
      setProductCategories(res.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch brand products:", error);
      setProducts([]);
      setProductsTotal(0);
    }
    setLoading(false);
    setProductsLoading(false);
  };

  const handleProductClick = async (product) => {
    if (!user || !token) {
      requireLogin(null, "Please login to shop and earn cashback on this product.");
      return;
    }
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        `${API}/track/click?item_type=brand_product&item_id=${product.id}`,
        {},
        { headers }
      );
      window.open(res.data.redirect_url, "_blank");
    } catch (error) {
      window.open(product.product_url, "_blank");
    }
  };

  const handleBrandClick = (brand, e) => {
    if (e) e.preventDefault();
    // ALWAYS open the in-app brand page (never external redirect from card)
    navigate(`/brands/${brand.id}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Brand listing view
  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <SiteHeader />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Partner Brands
            </h1>
            <p className="text-gray-500">
              Shop from your favorite brands and earn cashback on every purchase
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-8 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filteredBrands.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredBrands.map((brand, idx) => {
                // Random gradient colors for each brand card
                const gradients = [
                  "from-blue-500 to-indigo-600",
                  "from-purple-500 to-pink-500",
                  "from-fuchsia-500 to-pink-500",
                  "from-amber-500 to-red-500",
                  "from-cyan-500 to-blue-500",
                  "from-rose-500 to-pink-500",
                  "from-violet-500 to-purple-600",
                  "from-amber-500 to-amber-500",
                  "from-lime-500 to-green-500",
                  "from-fuchsia-500 to-purple-500",
                ];
                const gradient = gradients[idx % gradients.length];
                
                return (
                  <button
                    key={brand.id}
                    type="button"
                    onClick={(e) => handleBrandClick(brand, e)}
                    className="group text-left"
                    data-testid={`brand-card-${brand.id}`}
                  >
                    <Card className="border-0 shadow-sm hover:shadow-xl transition-all card-hover overflow-hidden">
                      {brand.logo_url ? (
                        <div className="h-24 bg-white flex items-center justify-center p-3 border-b border-gray-100 overflow-hidden">
                          <img
                            src={brand.logo_url}
                            alt={brand.name}
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              const parent = e.target.parentElement;
                              parent.className = `h-24 bg-gradient-to-br ${gradient} flex items-center justify-center`;
                              const initials = brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                              parent.innerHTML = `<span class="text-3xl font-bold text-white drop-shadow-lg">${initials}</span>`;
                            }}
                          />
                        </div>
                      ) : (
                        <div className={`h-24 bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                          <span className="text-3xl font-bold text-white drop-shadow-lg">
                            {brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="text-center">
                          <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {brand.name}
                          </h3>
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <Badge className="bg-primary-100 text-primary-700">
                              <Gift className="w-3 h-3 mr-1" />
                              {brand.cashback_percent}% Cashback
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-400">
                            {brand.product_count || 0} Products
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No brands found</p>
            </div>
          )}
        </div>
        <LoginRequiredDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          message={dialogMessage}
        />
      </div>
    );
  }

  // Brand products view
  return (
    <div className="min-h-screen bg-gray-50">
      <LoginRequiredDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        message={dialogMessage}
      />
      {/* Header */}
      <SiteHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-2xl" />
              ))}
            </div>
          </div>
        ) : selectedBrand ? (
          <>
            {/* Brand Header with Logo or Gradient */}
            <div className="mb-8 bg-white rounded-2xl shadow-sm overflow-hidden">
              {selectedBrand.logo_url ? (
                <div className="h-32 bg-white flex items-center justify-center p-6 border-b border-gray-100">
                  <img
                    src={selectedBrand.logo_url}
                    alt={selectedBrand.name}
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => {
                      const parent = e.target.parentElement;
                      parent.className = "h-32 bg-gradient-to-br from-primary-500 via-fuchsia-500 to-pink-600 flex items-center justify-center";
                      const initials = selectedBrand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                      parent.innerHTML = `<span class="text-5xl font-bold text-white drop-shadow-lg">${initials}</span>`;
                    }}
                  />
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-br from-primary-500 via-fuchsia-500 to-pink-600 flex items-center justify-center">
                  <span className="text-5xl font-bold text-white drop-shadow-lg">
                    {selectedBrand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="p-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {selectedBrand.name}
                </h1>
                {selectedBrand.description && (
                  <p className="text-gray-500 mb-3">{selectedBrand.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <Badge className="bg-primary-100 text-primary-700 px-3 py-1">
                    <Gift className="w-4 h-4 mr-1" />
                    {selectedBrand.cashback_percent}% Cashback
                  </Badge>
                  <Badge className="bg-secondary-100 text-secondary-700 px-3 py-1">
                    <Percent className="w-4 h-4 mr-1" />
                    Up to {selectedBrand.commission_percent}% Commission
                  </Badge>
                </div>
              </div>
            </div>

            {/* Filter / Search / Sort toolbar */}
            <div className="mb-6 bg-white rounded-2xl shadow-sm p-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={pFilters.search}
                    onChange={(e) => setPFilters({ ...pFilters, search: e.target.value, page: 1 })}
                    placeholder="Search products..."
                    className="pl-9"
                    data-testid="brand-products-search"
                  />
                </div>

                <Select
                  value={pFilters.category || "all"}
                  onValueChange={(v) => setPFilters({ ...pFilters, category: v === "all" ? "" : v, page: 1 })}
                >
                  <SelectTrigger data-testid="brand-products-category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {productCategories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  min="0"
                  value={pFilters.minPrice}
                  onChange={(e) => setPFilters({ ...pFilters, minPrice: e.target.value, page: 1 })}
                  placeholder="Min ₹"
                  data-testid="brand-products-min-price"
                />

                <Input
                  type="number"
                  min="0"
                  value={pFilters.maxPrice}
                  onChange={(e) => setPFilters({ ...pFilters, maxPrice: e.target.value, page: 1 })}
                  placeholder="Max ₹"
                  data-testid="brand-products-max-price"
                />

                <Select
                  value={pFilters.sort}
                  onValueChange={(v) => setPFilters({ ...pFilters, sort: v, page: 1 })}
                >
                  <SelectTrigger data-testid="brand-products-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="popular">Most Popular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Showing {products.length} of {productsTotal} products
              </p>
            </div>

            {/* Products Grid */}
            {productsLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((product, idx) => {
                  // Extract product name from URL if title is just "Product"
                  const displayTitle = product.title === "Product" 
                    ? product.product_url.split('/').filter(Boolean).pop()?.replace(/-/g, ' ').replace(/\?.*/,'').substring(0, 50) || "View Product"
                    : product.title;
                  
                  // Gradient for placeholder
                  const gradients = [
                    "from-blue-400 to-indigo-500",
                    "from-purple-400 to-pink-500",
                    "from-fuchsia-400 to-pink-500",
                    "from-amber-400 to-red-500",
                    "from-cyan-400 to-blue-500",
                    "from-rose-400 to-pink-500",
                  ];
                  const gradient = gradients[idx % gradients.length];
                  
                  return (
                    <Card
                      key={product.id}
                      className="border-0 shadow-sm hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
                      onClick={() => handleProductClick(product)}
                      data-testid={`product-card-${product.id}`}
                    >
                      <div className="relative aspect-square bg-gray-100">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={displayTitle}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                          />
                        ) : null}
                        <div className={`w-full h-full ${product.image_url ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br ${gradient}`}>
                          <span className="text-4xl font-bold text-white/50">
                            {selectedBrand.name.charAt(0)}
                          </span>
                        </div>
                        <Badge className="absolute top-3 left-3 bg-primary-500 text-white">
                          {product.cashback_percent}% Cashback
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors capitalize">
                          {displayTitle}
                        </h3>
                        {product.discounted_price ? (
                          <div className="flex items-baseline gap-2 mb-3">
                            <span className="text-lg font-bold text-gray-900">
                              ₹{product.discounted_price.toLocaleString()}
                            </span>
                            {product.original_price && product.original_price > product.discounted_price && (
                              <span className="text-sm text-gray-400 line-through">
                                ₹{product.original_price.toLocaleString()}
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 mb-3">View on {selectedBrand.name}</p>
                        )}
                        <Button
                          className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 group"
                          onClick={(e) => { e.stopPropagation(); handleProductClick(product); }}
                          data-testid={`visit-partner-${product.id}`}
                        >
                          Visit Partner
                          <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-2xl">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No products found matching your filters</p>
              </div>
            )}

            {/* Pagination */}
            {productsTotal > PRODUCTS_PER_PAGE && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pFilters.page <= 1}
                  onClick={() => setPFilters({ ...pFilters, page: pFilters.page - 1 })}
                  data-testid="brand-products-prev"
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600 px-4">
                  Page {pFilters.page} of {Math.max(1, Math.ceil(productsTotal / PRODUCTS_PER_PAGE))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pFilters.page >= Math.ceil(productsTotal / PRODUCTS_PER_PAGE)}
                  onClick={() => setPFilters({ ...pFilters, page: pFilters.page + 1 })}
                  data-testid="brand-products-next"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Brand not found</p>
            <Button onClick={() => navigate("/brands")} className="mt-4">
              View All Brands
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandsPage;
